import React from 'react';
import { Divider, Typography } from '@mui/material';
import { ArrowHand } from '@vedmarket/ui-kit';
import cx from 'classnames';
import { useNavigate } from 'react-router-dom';

import { useGetLatestHelpArticlesQuery } from '../../api/help-categories';
import { COLORS, ROUTES } from '../../config/constants';
import './right-panel.scss';

type RightPanelProps = {
    title: React.ReactNode;
    items: React.ReactNode[];
    onItemClick?: (index: number) => void;
    activeItemIndex?: number;
};

type RightPanelItemProps = {
    text: React.ReactNode;
    onClick?: () => void;
    isActive?: boolean;
    isLast?: boolean;
};

type RightPanelItem = {
    text: React.ReactNode;
    documentId?: string;
};

const RightPanelListItem = ({ text, onClick, isActive, isLast }: RightPanelItemProps) => (
    <li className={cx('right-panel__item', {
        'right-panel__item_active': isActive,
    })} onClick={onClick}>
        <ArrowHand/>&nbsp;
        <Typography variant='body2' sx={{
            color: isActive ? COLORS.BLUE : COLORS.BLACK,
            marginBottom: isLast ? '6px' : '16px',
            width: '278px',
            lineHeight: '22px',
        }}>
            {text}
        </Typography>
    </li>
);

const RightPanel = ({ title, items, onItemClick, activeItemIndex }: RightPanelProps) => {

    const navigate = useNavigate()
    const shouldShowLatestArticles = !onItemClick

    const { data } = useGetLatestHelpArticlesQuery(undefined, {
        skip: !shouldShowLatestArticles,
    })

    const panelItems: RightPanelItem[] = shouldShowLatestArticles && data?.data?.length
        ? data.data.map((article) => (
            {
                text: [
                    article.helpCategory?.name,
                    article.title,
                    article.subtitle,
                ].filter(Boolean).join(' → '),
                documentId: article.documentId,
            }
        ))
        : items.map((item) => ({
            text: item,
        }))

    const handleLatestArticleClick = (documentId?: string) => {
        if (documentId) {
            navigate(`${ROUTES.ARTICLE}?documentId=${documentId}`)
        }
    }

    return (
        <div className='right-panel'>
            <Typography variant='h6'>
                {title}
            </Typography>
            <Divider sx={{
                marginTop: 3,
            }}/>
            <ul className='right-panel__list'>
                {panelItems.map((item, index) => (
                    <RightPanelListItem
                        text={item.text}
                        onClick={onItemClick ? () => onItemClick(index) : () => handleLatestArticleClick(item.documentId)}
                        isActive={index === activeItemIndex}
                        isLast={index === panelItems.length - 1}
                        key={`${item.text}`}
                    />
                ))}
            </ul>
        </div>
    )
};

export default RightPanel;
